import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import { getImageUrl } from '../../services/listings';

// Format price for display (e.g., 5600 -> $5,600/month for rentals)
const formatPriceForDisplay = (price, listingType) => {
  if (!price) return '';
  
  // If price is already formatted (contains $), return as is
  if (price.toString().includes('$')) {
    return price;
  }
  
  // Convert to number and format
  const numericValue = parseFloat(price);
  if (isNaN(numericValue)) return price;
  
  const formatted = `$${numericValue.toLocaleString('en-US')}`;
  
  // Append /month for rentals
  if (listingType === 'rentals' || listingType === 'past-rentals') {
    return `${formatted}/month`;
  }
  
  return formatted;
};

const ListingCard = ({ listing, listingType, onEdit, onDelete }) => {
  const getFirstImage = () => {
    if (listingType === 'past-sales' || listingType === 'past-rentals') {
      if (!listing.image) return null;
      // Handle both ObjectId objects and strings
      const imageId = typeof listing.image === 'object' && listing.image?.toString 
        ? listing.image.toString() 
        : listing.image;
      return getImageUrl(imageId);
    }
    
    if (!listing.images || listing.images.length === 0) return null;
    
    // Handle both ObjectId objects and strings
    const firstImage = listing.images[0];
    const imageId = typeof firstImage === 'object' && firstImage?.toString 
      ? firstImage.toString() 
      : firstImage;
    
    return getImageUrl(imageId);
  };

  const imageUrl = getFirstImage();
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('ListingCard - listing:', {
      _id: listing._id,
      images: listing.images,
      image: listing.image,
      imageUrl,
      listingType,
    });
  }

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        bgcolor: '#ffffff',
        '&:hover': {
          borderColor: '#c0c0c0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }
      }}
    >
      {imageUrl ? (
        <CardMedia
          component="img"
          height="200"
          image={imageUrl}
          alt={listing.address || listing.title}
          sx={{
            objectFit: 'cover',
          }}
          onError={(e) => {
            console.error('❌ Image failed to load:', imageUrl, e);
            e.target.style.display = 'none';
          }}
          onLoad={() => {
            console.log('✅ Image loaded successfully:', imageUrl);
          }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            borderRadius: '12px 12px 0 0',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No image
          </Typography>
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, pb: 1.5, px: 2, pt: 2 }}>
        <Typography 
          variant="h6" 
          component="h2" 
          gutterBottom
          sx={{
            fontWeight: 500,
            fontSize: '0.9375rem',
            lineHeight: 1.4,
            mb: 1.5,
            color: '#1a1d29',
            letterSpacing: '-0.01em',
          }}
        >
          {listingType === 'short-term-rentals' ? listing.title : listing.address}
        </Typography>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            mb: 1.5,
            color: '#1a1d29',
            letterSpacing: '-0.02em',
          }}
        >
          {formatPriceForDisplay(listing.price, listingType)}
        </Typography>
        {listingType === 'short-term-rentals' ? (
          <>
            <Typography variant="body2" sx={{ mb: 0.75, color: '#6b7280', fontSize: '0.8125rem' }}>
              {listing.subtitle}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.75, color: '#6b7280', fontSize: '0.8125rem' }}>
              {listing.beds} beds • {listing.baths} baths • {listing.guests} guests
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#6b7280', fontSize: '0.8125rem' }}>
              {listing.location}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ mb: 1.5, color: '#6b7280', fontSize: '0.8125rem' }}>
            {listing.beds} beds • {listing.baths} baths • {listing.sqft?.toLocaleString()} sqft
          </Typography>
        )}
        <Box
          sx={{
            display: 'inline-block',
            px: 1.25,
            py: 0.375,
            borderRadius: 0.5,
            bgcolor: listing.status === 'Active' ? '#ecfdf5' : 
                     listing.status === 'Sold' ? '#eff6ff' :
                     listing.status === 'Rented' ? '#fef3c7' : '#f3f4f6',
            border: `1px solid ${listing.status === 'Active' ? '#d1fae5' : 
                     listing.status === 'Sold' ? '#dbeafe' :
                     listing.status === 'Rented' ? '#fde68a' : '#e5e7eb'}`,
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 500,
              fontSize: '0.6875rem',
              color: listing.status === 'Active' ? '#065f46' :
                     listing.status === 'Sold' ? '#1e40af' :
                     listing.status === 'Rented' ? '#92400e' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {listing.status || 'N/A'}
          </Typography>
        </Box>
      </CardContent>
      <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, borderTop: '1px solid #f3f4f6' }}>
        <Button
          variant="text"
          startIcon={<EditIcon />}
          onClick={() => onEdit(listing)}
          fullWidth
          sx={{
            borderRadius: 0.5,
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '0.875rem',
            color: '#1a1d29',
            '&:hover': {
              bgcolor: '#f5f7fa',
            }
          }}
        >
          Edit
        </Button>
        <IconButton
          onClick={() => onDelete(listing._id)}
          aria-label="delete"
          sx={{
            borderRadius: 0.5,
            color: '#6b7280',
            '&:hover': {
              bgcolor: '#fee2e2',
              color: '#dc2626',
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

export default ListingCard;


